import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import PageLayout from '@/components/PageLayout';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useExpenseCategories } from '@/contexts/ExpenseCategoriesContext';
import { FALLBACK_CATEGORIES } from '@/constants/expenseCategories';
import { Button } from '@/components/ui/button';
import { Edit, Trash2, Image as ImageIcon, X, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getSignedViewUrl } from '@/lib/getSignedViewUrl';
import { toast } from '@/components/ui/use-toast';

interface Expense {
  id: string;
  car_id: string;
  expense_date: string;
  category: string;
  amount_cents: number;
  odometer_reading: number | null;
  receipt_url: string | null;
  description: string | null;
  fuel_volume_liters: number | null;
  tank_fully_filled: boolean | null;
}

const ExpenseViewPage: React.FC = () => {
  const { expenseId } = useParams<{ expenseId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, formatCurrency } = useI18n();
  const expenseCategoriesResult = useExpenseCategories?.();
  const { categories: dbCategories = [] } = expenseCategoriesResult || {};
  
  const [expense, setExpense] = useState<Expense | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const panStartRef = React.useRef<{ x: number; y: number } | null>(null);

  const carId = searchParams.get('carId');

  const categoriesToUse = (dbCategories && dbCategories.length > 0) ? dbCategories : FALLBACK_CATEGORIES;

  useEffect(() => {
    if (!expenseId) return;

    const loadExpense = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('expenses')
          .select('*')
          .eq('id', expenseId)
          .is('deleted_at', null)
          .single();

        if (error) throw error;
        if (!data) {
          throw new Error('Expense not found');
        }

        setExpense(data as Expense);

        // Load receipt image if available
        if (data.receipt_url) {
          try {
            const signedUrlInfo = await getSignedViewUrl(data.receipt_url);
            setReceiptUrl(signedUrlInfo.url);
          } catch (error) {
            console.error('Error loading receipt:', error);
          }
        }
      } catch (error) {
        console.error('Error loading expense:', error);
        toast({
          title: t('common.error') || 'Error',
          description: t('common.loadFailed') || 'Failed to load expense',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadExpense();
  }, [expenseId, t]);

  // Handle full screen image zoom and pan
  useEffect(() => {
    if (!fullScreenImage) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setFullScreenImage(null);
        setImageZoom(1);
        setImageOffset({ x: 0, y: 0 });
      }
    };

    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setImageZoom(prev => Math.max(1, Math.min(5, prev + delta)));
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      if (imageZoom > 1) {
        panStartRef.current = { x: e.clientX - imageOffset.x, y: e.clientY - imageOffset.y };
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (panStartRef.current && imageZoom > 1) {
        setImageOffset({
          x: e.clientX - panStartRef.current.x,
          y: e.clientY - panStartRef.current.y,
        });
      }
    };

    const handleMouseUp = () => {
      panStartRef.current = null;
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('wheel', handleWheel, { passive: false });
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('wheel', handleWheel);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.overflow = 'unset';
    };
  }, [fullScreenImage, imageZoom, imageOffset]);

  const handleBack = () => {
    if (carId) {
      navigate(`/expenses/list/${carId}`);
    } else {
      navigate(-1);
    }
  };

  const handleEdit = () => {
    navigate(`/expenses/edit/${expenseId}?carId=${expense?.car_id}`);
  };

  const handleDelete = async () => {
    if (!expense) return;

    try {
      const { error } = await supabase
        .from('expenses')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', expense.id);

      if (error) throw error;

      toast({
        title: t('common.success') || 'Success',
        description: t('common.deleted') || 'Expense deleted successfully',
      });

      handleBack();
    } catch (error) {
      console.error('Failed to delete expense:', error);
      toast({
        title: t('common.error') || 'Error',
        description: t('common.deleteFailed') || 'Failed to delete expense',
        variant: 'destructive',
      });
    }
  };

  const getCategoryLabel = (category: string) => {
    const cat = categoriesToUse.find(c => c.value === category);
    return t(`category.${category}`) || cat?.label || category;
  };

  if (loading) {
    return (
      <PageLayout title={t('common.loading') || 'Loading...'} showBackButton onBack={handleBack}>
        <div className="min-h-screen bg-[#F5F5F5] font-lato text-black flex items-center justify-center px-4">
          <div className="text-gray-500 text-sm">{t('common.loading') || 'Loading expense...'}</div>
        </div>
      </PageLayout>
    );
  }

  if (!expense) {
    return (
      <PageLayout title={t('common.notFound') || 'Not Found'} showBackButton onBack={handleBack}>
        <div className="min-h-screen bg-[#F5F5F5] font-lato text-black flex flex-col items-center justify-center px-4 text-center">
          <h2 className="text-2xl font-bold mb-2">{t('expenses.notFound') || 'Expense Not Found'}</h2>
          <p className="text-gray-500 mb-6">{t('expenses.notFoundDescription') || 'The expense you\'re looking for doesn\'t exist'}</p>
          <Button onClick={handleBack} className="bg-primary">
            {t('actions.goBack') || 'Go Back'}
          </Button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={getCategoryLabel(expense.category)}
      showBackButton
      onBack={handleBack}
    >
      <div className="min-h-screen bg-[#F5F5F5] font-lato text-black px-4 py-4">
        <div className="max-w-md mx-auto space-y-5">
          {/* HEADER CARD */}
          <div className="bg-white rounded-2xl shadow-md p-5 border border-gray-100 relative">
            <div className="flex justify-between items-start">
              <div>
                <div className="inline-block px-3 py-1 rounded-full bg-gray-100 text-xs font-medium text-gray-700">
                  {getCategoryLabel(expense.category)}
                </div>
                <h1 className="text-2xl font-bold mt-2 text-gray-900">
                  {formatCurrency(expense.amount_cents / 100)}
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  {format(new Date(expense.expense_date), 'dd MMM yyyy')}
                </p>
                {expense.odometer_reading !== null && (
                  <p className="text-sm text-gray-700 mt-2">
                    {t('addCost.odometer') || 'Odometer (km)'}:{' '}
                    {expense.odometer_reading.toLocaleString()} km
                  </p>
                )}
              </div>

              <div className="flex flex-row gap-2 items-start">
                <button
                  type="button"
                  onClick={handleEdit}
                  aria-label={t('actions.edit') || 'Edit'}
                  className="h-8 w-8 rounded-full border border-gray-200 bg-white flex items-center justify-center shadow-sm hover:bg-gray-50 transition"
                >
                  <Edit className="w-4 h-4 text-gray-700" />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteDialogOpen(true)}
                  aria-label={t('actions.delete') || 'Delete'}
                  className="h-8 w-8 rounded-full border border-gray-200 bg-white flex items-center justify-center shadow-sm hover:bg-gray-50 transition"
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
            </div>
            {/* Category Icon in bottom-right corner */}
            <div className="absolute bottom-3 right-3 w-14 h-14 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center shadow-sm">
              {(() => {
                const cat = categoriesToUse.find(c => c.value === expense.category);
                if (cat?.icon_path) {
                  return (
                    <img
                      src={cat.icon_path}
                      alt={getCategoryLabel(expense.category)}
                      className="w-9 h-9 object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  );
                }
                return (
                  <span className="text-xs text-gray-500">
                    {getCategoryLabel(expense.category).charAt(0).toUpperCase()}
                  </span>
                );
              })()}
            </div>
          </div>

          {/* DESCRIPTION BLOCK */}
          {expense.description && (
            <>
              <h2 className="text-sm font-semibold text-gray-800 mb-2 ml-1">
                {t('expenses.description') || 'Description'}
              </h2>
              <div className="bg-white rounded-2xl shadow-md p-5 border border-gray-100">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {expense.description}
                </p>
              </div>
            </>
          )}

          {/* REFUELING DETAILS */}
          {expense.category === 'Refueling' && (
            <>
              <h2 className="text-sm font-semibold text-gray-800 mb-2 ml-1">
                {t('expenses.refuelingDetails') || 'Refueling Details'}
              </h2>
              <div className="bg-white rounded-2xl shadow-md p-5 border border-gray-100">
                <div className="space-y-2 text-sm">
                  {expense.fuel_volume_liters !== null && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">
                        {t('addCost.fuelVolume') || 'Fuel Volume'}
                      </span>
                      <span className="font-medium text-gray-900">
                        {expense.fuel_volume_liters.toFixed(3)} L
                      </span>
                    </div>
                  )}

                  {expense.tank_fully_filled !== null && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">
                        {t('addCost.tankFullyFilled') || 'Tank Fully Filled'}
                      </span>
                      <span className="font-medium text-gray-900">
                        {expense.tank_fully_filled
                          ? t('common.yes') || 'Yes'
                          : t('common.no') || 'No'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* RECEIPT CARD */}
          <>
            <h2 className="text-sm font-semibold text-gray-800 mb-2 ml-1">
              {t('expenses.receipt') || 'Receipt'}
            </h2>
            <div className="bg-white rounded-2xl shadow-md p-5 border border-gray-100">
              {receiptUrl ? (
                <div
                  className="rounded-xl overflow-hidden border border-gray-200 cursor-pointer hover:opacity-95 transition"
                  onClick={() => {
                    setFullScreenImage(receiptUrl);
                    setImageZoom(1);
                    setImageOffset({ x: 0, y: 0 });
                  }}
                >
                  <img
                    src={receiptUrl}
                    alt="Receipt"
                    className="w-full h-auto object-cover"
                  />
                </div>
              ) : (
                <p className="text-xs text-gray-500">
                  {t('expenses.noReceipt') || 'No receipt added'}
                </p>
              )}
            </div>
          </>
        </div>
      </div>

      {/* Full Screen Image */}
      {fullScreenImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center"
          onClick={() => {
            setFullScreenImage(null);
            setImageZoom(1);
            setImageOffset({ x: 0, y: 0 });
          }}
        >
          <div className="relative max-w-full max-h-full p-4">
            <button
              onClick={() => {
                setFullScreenImage(null);
                setImageZoom(1);
                setImageOffset({ x: 0, y: 0 });
              }}
              className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={fullScreenImage}
              alt="Receipt"
              className="max-w-full max-h-[90vh] object-contain"
              style={{
                transform: `scale(${imageZoom}) translate(${imageOffset.x / imageZoom}px, ${imageOffset.y / imageZoom}px)`,
                transition: panStartRef.current ? 'none' : 'transform 0.1s',
              }}
              draggable={false}
            />
            {imageZoom > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-xs bg-black/50 px-3 py-1 rounded-full z-10">
                {Math.round(imageZoom * 100)}%
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.confirmDelete') || 'Confirm Delete'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('expenses.confirmDelete') || 'Are you sure you want to delete this expense? This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('actions.cancel') || 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {t('actions.delete') || 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  );
};

export default ExpenseViewPage;

